import React, { useState } from 'react';
import {
  Box, Button, FormControl, FormLabel, Input, Heading, Text, useToast,
  Checkbox, Link, VStack
} from '@chakra-ui/react';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import axios from 'axios';

const API_BASE = process.env.REACT_APP_API_BASE_URL;
// Bump this when you materially change your Terms/Privacy
const TERMS_VERSION = '2025-10-21';

const Register = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [agreed, setAgreed] = useState(false);
  const [loading, setLoading] = useState(false);

  const toast = useToast();
  const navigate = useNavigate();

  const canSubmit =
    !!email && !!password && !!firstName && !!lastName && agreed && !loading;

  const handleRegister = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;

    setLoading(true);
    try {
      const response = await axios.post(`${API_BASE}/auth/register`, {
        email,
        password,
        role: 'user',
        first_name: firstName,
        last_name: lastName,
        company_name: companyName,
        consent: {
          acceptedTerms: true,
          termsVersion: TERMS_VERSION,
        },
      });

      const token = response.data.access_token;
      localStorage.setItem('token', token);

      // 🧠 Fetch user role (plan) using the token
      const userRes = await axios.get(`${API_BASE}/auth/me`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const user = userRes.data;
      localStorage.setItem('plan', user.role); // ⭐ Store plan level

      toast({
        title: 'Account created and logged in!',
        description: 'Redirecting to choose a plan...',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });

      navigate('/plans');
    } catch (err) {
      toast({
        title: 'Registration failed.',
        description: err.response?.data?.detail || err.message,
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box maxW="md" mx="auto" mt={20} p={6} borderWidth={1} borderRadius="lg">
      <Heading mb={6}>Register</Heading>
      <form onSubmit={handleRegister}>
        <VStack align="stretch" spacing={4}>
          <FormControl id="firstName" isRequired>
            <FormLabel>First Name</FormLabel>
            <Input
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
            />
          </FormControl>

          <FormControl id="lastName" isRequired>
            <FormLabel>Last Name</FormLabel>
            <Input
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
            />
          </FormControl>

          <FormControl id="companyName">
            <FormLabel>Company Name</FormLabel>
            <Input
              value={companyName}
              onChange={(e) => setCompanyName(e.target.value)}
            />
          </FormControl>

          <FormControl id="email" isRequired>
            <FormLabel>Email</FormLabel>
            <Input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </FormControl>

          <FormControl id="password" mb={2} isRequired>
            <FormLabel>Password</FormLabel>
            <Input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
          </FormControl>

          {/* ✅ Required clickwrap */}
          <Checkbox
            isChecked={agreed}
            onChange={(e) => setAgreed(e.target.checked)}
          >
            I agree to ShelfKey’s{' '}
            <Link
              as={RouterLink}
              to="/terms"
              target="_blank"
              textDecoration="underline"
              color="#1C4C3F"
            >
              Terms &amp; Conditions
            </Link>{' '}
            and{' '}
            <Link
              as={RouterLink}
              to="/privacy"
              target="_blank"
              textDecoration="underline"
              color="#1C4C3F"
            >
              Privacy Policy
            </Link>
            .
          </Checkbox>

          <Button
            type="submit"
            colorScheme="green"
            width="full"
            isLoading={loading}
            loadingText="Creating account..."
            isDisabled={!canSubmit}
          >
            Sign Up
          </Button>

          {/* Optional gentle reminder */}
          <Text fontSize="sm" opacity={0.7}>
            AI-generated insights are for informational purposes only and should not replace
            professional judgment.
          </Text>
        </VStack>
      </form>

      <Text mt={4}>
        Already have an account?{' '}
        <a href="/login" style={{ color: '#479F7A' }}>Log in</a>
      </Text>
    </Box>
  );
};

export default Register;
